import { useState, useEffect, useCallback } from 'react';
import { QAItem, FilterType } from '../types/qa';
import { getQuestionAnswers } from '../utils/questionnaireApi';

export const useQAData = (
  availableFilters: FilterType[] = ['liked', 'edited', 'rejected', 'approved', 'archived'],
  excludeAdminStatuses: boolean = true,
  onlyMine: boolean = false
) => {
  const [qaItems, setQaItems] = useState<QAItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [activeFilter, setActiveFilter] = useState<FilterType>(null);

  const fetchQAData = useCallback(async (pageNum: number, pageSize: number, filter: FilterType) => {
    setLoading(true);
    setError(null);
    try {
      // Build query parameters
      const params = new URLSearchParams({
        page: (pageNum + 1).toString(),
        page_size: pageSize.toString(),
      });
      
      if (onlyMine) {
        params.append('only_mine', 'true');
      }

      // Determine if we should exclude admin statuses (for todo list view)
      const isAdminStatusFilter = filter === 'approved' || filter === 'archived';
      
      // Add filter parameters based on active filter
      if (filter) {
        switch (filter) {
          case 'liked':
            params.append('feedback_rating', 'LIKED');
            break;
          case 'rejected':
            params.append('feedback_rating', 'DISLIKED');
            break;
          case 'edited':
            params.append('has_edited_ans', 'true');
            break;
          case 'approved':
            params.append('admin_status', 'APPROVED');
            break;
          case 'archived':
            params.append('admin_status', 'ARCHIVED');
            break;
        }
      }
      
      // By default (no filter or non-admin-status filter), exclude approved/reviewed/archived for todo list view
      // But only if excludeAdminStatuses is true (for review page, not for view client questions)
      if (!isAdminStatusFilter && excludeAdminStatuses) {
        params.append('exclude_admin_status', 'APPROVED');
        params.append('exclude_admin_status', 'REVIEWED');
        params.append('exclude_admin_status', 'ARCHIVED');
      }

      const data = await getQuestionAnswers(params);
      setQaItems(data.items);
      setTotal(data.total);
    } catch (err) {
      setError('Failed to load questions. Please try again.');
      console.error('Error fetching QA data:', err);
    } finally {
      setLoading(false);
    }
  }, [excludeAdminStatuses, onlyMine]);

  useEffect(() => {
    fetchQAData(page, rowsPerPage, activeFilter);
  }, [page, rowsPerPage, activeFilter, fetchQAData]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterClick = (filter: FilterType) => {
    const newFilter = activeFilter === filter ? null : filter;
    setActiveFilter(newFilter);
    setPage(0);
  };

  const handleResetAll = () => {
    setActiveFilter(null);
    setPage(0);
  };

  return {
    qaItems,
    loading,
    error,
    page,
    rowsPerPage,
    total,
    activeFilter,
    setError,
    handleChangePage,
    handleChangeRowsPerPage,
    handleFilterClick,
    handleResetAll,
    availableFilters,
  };
};

